import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';
import { Button } from './ui/button';

export const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show banner after a delay
      setTimeout(() => {
        if (!standalone) {
          setShowInstallBanner(true);
        }
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setDeferredPrompt(null);
      setShowInstallBanner(false);
      setIsStandalone(true);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
      setShowInstallBanner(false);
    }
  };

  const dismissBanner = () => {
    setShowInstallBanner(false);
    // Don't show again for this session
    sessionStorage.setItem('pwa-banner-dismissed', 'true');
  };

  // Don't show if already installed or dismissed
  if (isStandalone || sessionStorage.getItem('pwa-banner-dismissed')) {
    return null;
  }

  // Show iOS instructions
  if (isIOS && !isStandalone) {
    return showInstallBanner ? (
      <div className="fixed bottom-4 left-4 right-4 z-50 glass p-4 border border-gold/30 animate-slide-up" data-testid="pwa-ios-banner">
        <button 
          onClick={dismissBanner}
          className="absolute top-2 right-2 text-muted-foreground hover:text-white"
        >
          <X size={18} />
        </button>
        <div className="flex items-start gap-3">
          <div className="p-2 bg-gold/20">
            <Smartphone size={24} className="text-gold" />
          </div>
          <div>
            <p className="text-white font-medium mb-1">Install Assagart</p>
            <p className="text-sm text-muted-foreground">
              Tap <span className="inline-flex items-center px-1 bg-[#1A1A1A] rounded">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12l7-7 7 7"/>
                  <rect x="4" y="18" width="16" height="2" rx="1"/>
                </svg>
              </span> then "Add to Home Screen"
            </p>
          </div>
        </div>
      </div>
    ) : null;
  }

  // Show install banner for Android/Desktop
  if (!showInstallBanner || !deferredPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 glass p-4 border border-gold/30 animate-slide-up" data-testid="pwa-install-banner">
      <button 
        onClick={dismissBanner}
        className="absolute top-2 right-2 text-muted-foreground hover:text-white"
      >
        <X size={18} />
      </button>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gold/20">
            <Download size={24} className="text-gold" />
          </div>
          <div>
            <p className="text-white font-medium">Install Assagart App</p>
            <p className="text-sm text-muted-foreground">Add to home screen for quick access</p>
          </div>
        </div>
        <Button onClick={handleInstall} className="btn-primary whitespace-nowrap" data-testid="pwa-install-btn">
          Install
        </Button>
      </div>
    </div>
  );
};
